import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/data/site";

const Logo = ({ light = false }) => (
  <Link to="/" data-testid="nav-logo" className="flex flex-col leading-none">
    <span
      className={`text-2xl font-bold tracking-tight ${
        light ? "text-white" : "text-brand-green"
      }`}
    >
      Faf's
    </span>
    <span
      className={`text-[10px] tracking-[0.3em] uppercase font-medium ${
        light ? "text-white/70" : "text-brand-slate"
      }`}
    >
      by Rivella
    </span>
  </Link>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-black/5 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="container-px mx-auto max-w-[1400px] flex items-center justify-between">
        <Logo />

        <div className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((link) => {
            const active = location.pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-sm font-medium transition-colors relative group ${
                  active ? "text-brand-green" : "text-brand-ink hover:text-brand-green"
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1.5 left-0 h-[2px] bg-brand-green transition-all duration-300 ${
                    active ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            );
          })}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={CONTACT.whatsappOrderHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-whatsapp"
            className="rounded-full bg-brand-green px-6 py-2.5 text-sm font-medium text-white transition-all duration-300 hover:bg-brand-greenDark hover:-translate-y-0.5"
          >
            Order on WhatsApp
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-brand-ink"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden fixed inset-0 top-[60px] bg-white animate-fade-in"
        >
          <div className="container-px py-8 flex flex-col gap-1">
            {NAV_LINKS.map((link, i) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-2xl font-medium text-brand-ink py-3 border-b border-black/5 animate-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                {link.label}
              </Link>
            ))}
            <a
              href={CONTACT.whatsappOrderHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-cta-whatsapp"
              className="mt-6 rounded-full bg-brand-green px-6 py-4 text-center text-base font-medium text-white"
            >
              Order on WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
